import axios from 'axios';
import useAuth from '../../Users/hooks/useAuth';
import { useState } from "react";

const UploadFile = ({book, successHandler}) => {
    const [file, setFile] = useState()
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState("");

    function handleChange(event) {
        setFile(event.target.files[0]);
        setError(""); // Clear any previous errors when new file is selected
    }

    function handleSubmit(event) {
        event.preventDefault();
        
        if (!file) {
            setError("Please select a file first");
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);
        setError("");

        const token = localStorage.getItem("token");
        const url = `${process.env.REACT_APP_USER_API_URL}/upload/upload_file/${book.language}/${book.author}/${book.title}/${book.type}`;
        const formData = new FormData();
        formData.append('uploaded_file', file);

        const config = {
            headers: {
                'content-type': file.type,
                Authorization: `Bearer ${token}`
            },
            onUploadProgress: function(progressEvent) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(percentCompleted);
            }
        };

        axios.post(url, formData, config)
            .then((response) => {
                successHandler(true);
                setFile(null); // Reset file input
                setUploadProgress(0);
                setIsUploading(false);
                
                // Reset file input element
                const fileInput = document.querySelector('input[type="file"]');
                if (fileInput) fileInput.value = '';
            })
            .catch((error) => {
                console.error("Error uploading file: ", error);
                setError(error.response?.data?.message || "Upload failed. Please try again.");
                setIsUploading(false);
                setUploadProgress(0);
            });
    }  

    return (
        <div className="flex flex-col p-6 bg-white border border-gray-200 rounded-xl shadow-sm">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">Upload Audio File</h3>
            
            <form className="flex flex-col space-y-4" onSubmit={handleSubmit}>
                {/* File Input */}
                <div className="flex flex-col items-center">
                    <label className="relative cursor-pointer">
                        <input 
                            className="hidden" 
                            type="file" 
                            onChange={handleChange}
                            accept="audio/*"
                            disabled={isUploading}
                        />
                        <div className={`
                            px-6 py-4 border-2 border-dashed rounded-lg text-center transition-all duration-200
                            ${file 
                                ? 'border-green-400 bg-green-50 text-green-700' 
                                : 'border-gray-300 bg-gray-50 text-gray-600 hover:border-blue-400 hover:bg-blue-50'
                            }
                            ${isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}>
                            <svg 
                                className="w-8 h-8 mx-auto mb-2" 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" 
                                />
                            </svg>
                            <span className="font-medium">
                                {file ? file.name : "Choose audio file"}
                            </span>
                            <p className="text-sm mt-1">
                                {file ? "File selected" : "Click to browse or drag and drop"}
                            </p>
                        </div>
                    </label>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-600 text-sm text-center font-medium">{error}</p>
                    </div>
                )}

                {/* Upload Button */}
                <button 
                    className={`
                        w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200
                        ${!file || isUploading
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                            : 'bg-blue-500 hover:bg-blue-600 text-white shadow-sm hover:shadow-md'
                        }
                    `}
                    type="submit"
                    disabled={!file || isUploading}
                >
                    {isUploading ? (
                        <div className="flex items-center justify-center gap-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Uploading...
                        </div>
                    ) : (
                        "Upload File"
                    )}
                </button>

                {/* Progress Bar */}
                {isUploading && (
                    <div className="space-y-2">
                        <div className="flex justify-between text-sm text-gray-600">
                            <span>Upload progress:</span>
                            <span className="font-medium">{uploadProgress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div 
                                className="bg-blue-500 h-2.5 rounded-full transition-all duration-300 ease-out"
                                style={{ width: `${uploadProgress}%` }}
                            ></div>
                        </div>
                    </div>
                )}

                {/* Success Message */}
                {uploadProgress === 100 && !isUploading && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-600 text-sm text-center font-medium">
                            ✅ File uploaded successfully!
                        </p>
                    </div>
                )}
            </form>

            {/* File Info */}
            {file && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between text-sm text-gray-600">
                        <span className="font-medium">File info:</span>
                        <span>{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 truncate">
                        Type: {file.type || "Unknown"}
                    </div>
                </div>
            )}
        </div>
    );
}

export default UploadFile;